export interface ApigeeFault {
  faultstring: string;
  detail?: {
    errorcode?: string;
  };
}

function parseApigeeResponse(responseBody: string) {
  try {
    const json = JSON.parse(responseBody);
    if (json.fault?.faultstring) {
      return json.fault as ApigeeFault;
    }
  } catch (err) {
    // ignored
  }
  // this was likely not a proper apigee error response
  return undefined;
}

function apigeeFaultErrorMessage(fault: ApigeeFault) {
  let message = fault.faultstring;
  const errorCode = fault.detail?.errorcode;
  if (errorCode) {
    message += `; ${errorCode}`;
  }
  return message;
}

export class ApigeeError extends Error {
  readonly fault: ApigeeFault;

  constructor(fault: ApigeeFault) {
    super(apigeeFaultErrorMessage(fault)); // 'Error' breaks prototype chain here
    this.fault = fault;
    Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
    // If the name is anything but an empty string, it is prepended to the error message. This
    // can be obnoxious when embedding messages into other strings via toString. This prevents
    // that from occurring. If a consumer wants a prefix, they can add it themselves or just
    // set name again.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/toString#Description
    this.name = 'ApigeeError';
  }

  static fromResponseBody(responseBody: string) {
    const fault = parseApigeeResponse(responseBody);
    if (!fault) {
      return undefined;
    }
    return new ApigeeError(fault);
  }

  static throwIfFault(responseBody: string) {
    const err = ApigeeError.fromResponseBody(responseBody);
    if (err) {
      throw err;
    }
  }
}

export class AuthError extends Error {
  readonly cause: Error | undefined;

  constructor(name: string, message?: string, cause?: Error) {
    super(message); // 'Error' breaks prototype chain here
    Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
    this.cause = cause;
    // If the name is anything but an empty string, it is prepended to the error message. This
    // can be obnoxious when embedding messages into other strings via toString. This prevents
    // that from occurring. If a consumer wants a prefix, they can add it themselves or just
    // set name again.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/toString#Description
    this.name = name;
  }

  toString() {
    let s = super.toString();
    if (this.cause) {
      s = `${s}\nCaused by: ${this.cause.toString()}`;
    }
    return s;
  }
}

export class NetworkError extends AuthError {
  constructor(message?: string) {
    super('Network Error', message);
  }
}

export class RefreshError extends AuthError {
  readonly responseBody: string;

  constructor(responseBody: string) {
    let message = 'Token refresh failed.';
    const fault = parseApigeeResponse(responseBody);
    if (fault) {
      message += ' ' + fault.faultstring;
    }
    super('Refresh Error', message);
    this.responseBody = responseBody;
  }
}

export class UnsupportedAuthTypeError extends AuthError {
  constructor(authType: string) {
    super(
      'Unconfigured AuthType',
      `Auth type was determined to be '${authType}', which is unconfigured.`,
    );
  }
}

export class UnknownAuthTypeError extends AuthError {
  constructor(authType: string, validTypes: string[]) {
    super('Unknown AuthType', `'${authType}' must be one of: ${validTypes.join(', ')}`);
  }
}
